import { FilterOptions, Sort } from '@vimeo/comments-ui';
import { useDeleteAlbumVideoComment } from '@vimeo/gctl-api/hooks/useAlbumVideoComment';
import { useDeleteVideoComment } from '@vimeo/gctl-api/hooks/useVideoComment';
import { useDeleteVideoPrivateComment } from '@vimeo/gctl-api/hooks/useVideoPrivateComment';
import { Video as VideoData } from '@vimeo/gctl-api/models/components/schemas/video/Video';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { AnalyticsPropsType } from '../../CommentsCommon/types';
import { bpDeleteComment } from '../../utils/analytics';
import {
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  idFromUri,
} from '../../utils/helper';
import { useVideoData } from '../use-video-data';
import { useViewer } from '../use-viewer';
import { usePrivateComments, usePublicComments } from './use-get-comments';
import { useGetPrivateCommentsCount, useGetPublicCommentsCount } from './use-get-comments-count';
import { useVideoPrivacy } from './use-video-privacy';

/**
 * A customized hook to handle deleting Public and Private comments data depending on privacy
 */
export function useDeleteComments(
  clipRequestId: string,
  onFailure: () => void,
  isPublic: boolean,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const { videoData } = useVideoData(clipRequestId, showcaseId, reviewId);
  const useDeleteCommentsHook = isPublic ? useDeletePublicComment : useDeletePrivateComment;
  return useDeleteCommentsHook(
    clipRequestId,
    onFailure,
    videoData,
    sort,
    analyticsProps,
    filterOptions,
    showcaseId,
    reviewId,
  );
}

/**
 * A hook to handle deleting a Private comments
 */
export function useDeletePrivateComment(
  clipRequestId: string,
  onFailure: () => void,
  videoData?: VideoData,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const [deleteVideoPrivateComments, { loading, error }] = useDeleteVideoPrivateComment();
  const { privacy } = useVideoPrivacy(clipRequestId, undefined, reviewId);
  const { revalidateComments } = usePrivateComments(
    clipRequestId,
    sort,
    filterOptions,
    undefined,
    reviewId,
  );
  const { revalidateCommentCount } = useGetPrivateCommentsCount(
    clipRequestId,
    {
      shouldFetch: true,
      videoVersionUri: filterOptions?.videoVersionUri,
      targetApiVersion: filterOptions?.targetApiVersion,
    },
    reviewId,
  );
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const password = getReviewPasswordHashFromCookie(reviewId);

  const deleteComment = async (uri: string, isTimeCoded?: boolean) => {
    await deleteVideoPrivateComments({
      where: {
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      query: { reviewId, password },
    });
    revalidateData(uri, isTimeCoded);
  };

  const revalidateData = (uri: string, isTimeCoded?: boolean) => {
    if (!!error && !loading) {
      onFailure();
    }
    if (!loading && !error) {
      if (analyticsProps) {
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(uri) as unknown as number,
            is_reply: false,
          },
        };
        bpDeleteComment(
          videoId,
          privacy?.view,
          false,
          analytics,
          viewer,
          isTimeCoded,
          videoData?.uploader?.link,
        ); // fire event on success of deleted comment
      }
      revalidateComments();
      revalidateCommentCount();
    }
  };

  return {
    deleteComment,
    deleteCommentLoading: loading,
    deleteCommentError: error,
  };
}

/**
 * A hook to handle deleting a Public comment
 */
export function useDeletePublicComment(
  clipRequestId: string,
  onFailure: () => void,
  videoData?: VideoData,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
) {
  const [deletePublicComment, commentState] = useDeleteVideoComment();
  const [deleteAlbumPublicComment, albumCommentState] = useDeleteAlbumVideoComment();
  const { loading, error } = showcaseId ? albumCommentState : commentState;

  const { privacy } = useVideoPrivacy(clipRequestId, showcaseId);
  const { revalidateComments } = usePublicComments(clipRequestId, sort, filterOptions, showcaseId);
  const { revalidateCommentCount } = useGetPublicCommentsCount(
    clipRequestId,
    {
      shouldFetch: true,
    },
    showcaseId,
  );
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);

  const password = getPasswordHashFromCookie(clipRequestId);

  const deleteComment = async (uri: string) => {
    await deletePublicComment({
      where: {
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      ...(password ? { query: { password } } : {}),
    });
    revalidateData(uri);
  };

  const deleteAlbumComment = async (uri: string) => {
    if (!showcaseId) return;
    const showcasePassword = getShowcasePasswordHashFromCookie(showcaseId);

    await deleteAlbumPublicComment({
      where: {
        albumId: showcaseId,
        videoId: clipRequestId as unknown as number,
        commentId: idFromUri(uri) as unknown as number,
      },
      ...(showcasePassword ? { query: { password: showcasePassword } } : {}),
    });
    revalidateData(uri);
  };

  const revalidateData = (uri: string) => {
    if (!!error && !loading) {
      onFailure();
    }
    if (!loading && !error) {
      if (analyticsProps) {
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(uri) as unknown as number,
            is_reply: false,
          },
        };
        bpDeleteComment(
          videoId,
          privacy?.view,
          true,
          analytics,
          viewer,
          null,
          videoData?.uploader?.link,
        ); // fire event on success of deleted comment
      }
      revalidateComments();
      revalidateCommentCount();
    }
  };

  return {
    deleteComment: showcaseId ? deleteAlbumComment : deleteComment,
    deleteCommentLoading: loading,
    deleteCommentError: error,
  };
}
